import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import defaultsDeep from 'lodash.defaultsdeep';
import makeToolboxXML from '../lib/make-toolbox-xml';
import PropTypes from 'prop-types';
import React from 'react';
import VMScratchBlocks from '../lib/blocks';
import VM from 'openblock-vm';
import MessageBoxType from '../lib/message-box.js';

import log from '../lib/log.js';
import Prompt from './prompt.jsx';
import BlocksComponent from '../components/blocks/blocks.jsx';
import ExtensionLibrary from './extension-library.jsx';
import DeviceLibrary from './device-library.jsx';
import extensionData from '../lib/libraries/extensions/index.jsx';
import CustomProcedures from './custom-procedures.jsx';
import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {BLOCKS_DEFAULT_SCALE, STAGE_DISPLAY_SIZES} from '../lib/layout-constants';
import DropAreaHOC from '../lib/drop-area-hoc.jsx';
import DragConstants from '../lib/drag-constants';
import defineDynamicBlock from '../lib/define-dynamic-block';
import {getGeneratorNameFromDeviceType} from '../lib/code-generator';
import {defineDemoBlocks} from '../lib/demo-blocks';
import {defineDemoGenerators} from '../lib/demo-generators';
import {setWorkspace} from '../lib/scanned-devices-store';

import {connect} from 'react-redux';
import {updateToolbox, setIsUpdating} from '../reducers/toolbox';
import {showAlertWithTimeout} from '../reducers/alerts';
import {activateColorPicker} from '../reducers/color-picker';
import {closeExtensionLibrary, openSoundRecorder, openConnectionModal, closeDeviceLibrary} from '../reducers/modals';
import {activateCustomProcedures, deactivateCustomProcedures} from '../reducers/custom-procedures';
import {updateMetrics} from '../reducers/workspace-metrics';
import {setCodeEditorValue} from '../reducers/code';
import {setDeviceId, setDeviceName, setDeviceType} from '../reducers/device';
import {setSupportSwitchMode} from '../reducers/program-mode';
import {setBaudrate} from '../reducers/hardware-console';

import {
    activateTab,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

const addFunctionListener = (object, property, callback) => {
    const oldFn = object[property];
    object[property] = function (...args) {
        const result = oldFn.apply(this, args);
        callback.apply(this, result);
        return result;
    };
};

const DroppableBlocks = DropAreaHOC([
    DragConstants.BACKPACK_CODE
])(BlocksComponent);

class Blocks extends React.Component {
    constructor (props) {
        super(props);
        this.ScratchBlocks = VMScratchBlocks(props.vm);
        bindAll(this, [
            'attachVM',
            'detachVM',
            'getToolboxXML',
            'handleBlocksInfoUpdate',
            'handleCategorySelected',
            'handleConnectionModalStart',
            'handleDeviceExtensionAdded',
            'handleDeviceExtensionRemoved',
            'handleDeviceSelected',
            'handleDrop',
            'handleScratchExtensionAdded',
            'handleScratchExtensionRemoved',
            'handleStatusButtonUpdate',
            'handleOpenSoundRecorder',
            'handlePromptStart',
            'handlePromptCallback',
            'handlePromptClose',
            'handleToolboxUploadFinish',
            'handleCustomProceduresClose',
            'onCodeNeedUpdate',
            'onScriptGlowOn',
            'onScriptGlowOff',
            'onBlockGlowOn',
            'onBlockGlowOff',
            'onProgramModeUpdate',
            'onTargetsUpdate',
            'onVisualReport',
            'onActivateColorPicker',
            'onWorkspaceUpdate',
            'onWorkspaceMetricsChange',
            'setBlocks',
            'setLocale',
            'workspaceToCode',
            'overrideBlockMessages'
        ]);
        this.ScratchBlocks.prompt = this.handlePromptStart;
        this.ScratchBlocks.statusButtonCallback = this.handleConnectionModalStart;
        this.ScratchBlocks.recordSoundCallback = this.handleOpenSoundRecorder;
        this.ScratchBlocks.alert = message => {
            this.props.onShowMessageBox(MessageBoxType.alert, message);
        };

        this.state = {
            prompt: null
        };
        this.onTargetsUpdate = debounce(this.onTargetsUpdate, 100);
        this.toolboxUpdateQueue = [];
    }
    componentDidMount () {
        this.ScratchBlocks.FieldColourSlider.activateEyedropper_ = this.onActivateColorPicker;
        this.ScratchBlocks.Procedures.externalProcedureDefCallback = this.props.onActivateCustomProcedures;
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);

        // Define demo blocks
        defineDemoBlocks(this.ScratchBlocks);
        // Define demo generators
        defineDemoGenerators(this.ScratchBlocks);

        // Override only block messages (texts), keep styles intact
        this.overrideBlockMessages();

        const workspaceConfig = defaultsDeep({},
            Blocks.defaultOptions,
            this.props.options,
            {rtl: this.props.isRtl, toolbox: this.props.toolboxXML}
        );
        this.workspace = this.ScratchBlocks.inject(this.blocks, workspaceConfig);

        // Set workspace reference for scanned devices store to enable dynamic dropdown refresh
        setWorkspace(this.workspace);

        // Register buttons under new callback keys for creating variables,
        // lists, and procedures from extensions.

        const toolboxWorkspace = this.workspace.getFlyout().getWorkspace();

        const varListButtonCallback = type =>
            (() => this.ScratchBlocks.Variables.createVariable(this.workspace, null, type));
        const procButtonCallback = () => {
            this.ScratchBlocks.Procedures.createProcedureDefCallback_(this.workspace);
        };

        toolboxWorkspace.registerButtonCallback('MAKE_A_VARIABLE', varListButtonCallback(''));
        toolboxWorkspace.registerButtonCallback('MAKE_A_LIST', varListButtonCallback('list'));
        toolboxWorkspace.registerButtonCallback('MAKE_A_PROCEDURE', procButtonCallback);

        // Store the xml of the toolbox that is actually rendered.
        // This is used in componentDidUpdate instead of prevProps, because
        // the xml can change while e.g. on the costumes tab.
        this._renderedToolboxXML = this.props.toolboxXML;

        // Store the programmode.
        // This is used in componentDidUpdate to control should update toolbox xml.
        this._programMode = this.props.isRealtimeMode;

        // Creat a flag to control insert spaces that have no actual function after a device is added,
        // to forcefully update the blocks in the flyout area. In order to avoid the problem of the
        // rendering of the flyout blocks is not triggered because the contents of the toolbox are
        // exactly the same when the device blocks have the same opcode but different drop - down menus.
        this.deviceFakeToolboxHead = '';

        // we actually never want the workspace to enable "refresh toolbox" - this basically re-renders the
        // entire toolbox every time we reset the workspace.  We call updateToolbox as a part of
        // componentDidUpdate so the toolbox will still correctly be updated
        this.setToolboxRefreshEnabled = this.workspace.setToolboxRefreshEnabled.bind(this.workspace);
        this.workspace.setToolboxRefreshEnabled = () => {
            this.setToolboxRefreshEnabled(false);
        };

        // @todo change this when blockly supports UI events
        addFunctionListener(this.workspace, 'translate', this.onWorkspaceMetricsChange);
        addFunctionListener(this.workspace, 'zoom', this.onWorkspaceMetricsChange);

        this.attachVM();
        // Only update blocks/vm locale when visible to avoid sizing issues
        // If locale changes while not visible it will get handled in didUpdate
        if (this.props.isVisible) {
            this.setLocale();
        }
    }
    shouldComponentUpdate (nextProps, nextState) {
        return (
            this.state.prompt !== nextState.prompt ||
            this.props.isVisible !== nextProps.isVisible ||
            this._renderedToolboxXML !== nextProps.toolboxXML ||
            this.props.extensionLibraryVisible !== nextProps.extensionLibraryVisible ||
            this.props.customProceduresVisible !== nextProps.customProceduresVisible ||
            this.props.locale !== nextProps.locale ||
            this.props.anyModalVisible !== nextProps.anyModalVisible ||
            this.props.stageSize !== nextProps.stageSize ||
            this.props.isRealtimeMode !== nextProps.isRealtimeMode ||
            this.props.isCodeEditorLocked !== nextProps.isCodeEditorLocked
        );
    }
    componentDidUpdate (prevProps) {
        // If any modals are open, call hideChaff to close z-indexed field editors
        if (this.props.anyModalVisible && !prevProps.anyModalVisible) {
            this.ScratchBlocks.hideChaff();
        }

        // If program mode changed, call functio to update the toolbox
        if (this.props.isRealtimeMode !== this._programMode) {
            // Clear possible errors witch print in to code editor.
            this.props.onSetCodeEditorValue('');
            this.onProgramModeUpdate();
        }

        // Only rerender the toolbox when the blocks are visible and the xml is
        // different from the previously rendered toolbox xml.
        // Do not check against prevProps.toolboxXML because that may not have been rendered.
        if (this.props.isVisible && this.props.toolboxXML !== this._renderedToolboxXML) {
            this.requestToolboxUpdate();
        }

        if (this.props.isCodeEditorLocked && this.props.isCodeEditorLocked !== prevProps.isCodeEditorLocked) {
            this.onCodeNeedUpdate();
        }

        if (this.props.isVisible === prevProps.isVisible) {
            if (this.props.stageSize !== prevProps.stageSize) {
                // force workspace to redraw for the new stage size
                window.dispatchEvent(new Event('resize'));
            }
            return;
        }
        // @todo hack to resize blockly manually in case resize happened while hidden
        // @todo hack to reload the workspace due to gui bug #413
        if (this.props.isVisible) { // Scripts tab
            this.workspace.setVisible(true);
            if (prevProps.locale !== this.props.locale || this.props.locale !== this.props.vm.getLocale()) {
                // call setLocale if the locale has changed, or changed while the blocks were hidden.
                // vm.getLocale() will be out of sync if locale was changed while not visible
                this.setLocale();
            } else {
                this.props.vm.refreshWorkspace();
                this.requestToolboxUpdate();
            }

            window.dispatchEvent(new Event('resize'));
        } else {
            this.workspace.setVisible(false);
        }
    }
    componentWillUnmount () {
        this.detachVM();
        if (this.workspace) {
            this.workspace.dispose();
        }
        clearTimeout(this.toolboxUpdateTimeout);
        clearTimeout(this.getXMLAndUpdateToolboxTimeout);
    }
    requestToolboxUpdate () {
        clearTimeout(this.toolboxUpdateTimeout);
        this.toolboxUpdateTimeout = setTimeout(() => {
            this.updateToolbox();
        }, 0);
    }
    setLocale (refreshWorkspace = true) {
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);
        this.props.vm.setLocale(this.props.locale, this.props.messages)
            .then(() => {
                this.workspace.getFlyout().setRecyclingEnabled(false);
                if (refreshWorkspace) {
                    this.props.vm.refreshWorkspace();
                }
                this.requestToolboxUpdate();
                this.withToolboxUpdates(() => {
                    this.workspace.getFlyout().setRecyclingEnabled(true);
                });
            });
    }
    onProgramModeUpdate () {
        if (this.props.isRealtimeMode) {
            this.ScratchBlocks.ProgramMode.setProgramMode(this.ScratchBlocks.ProgramMode.REALTIME);
        } else {
            this.ScratchBlocks.ProgramMode.setProgramMode(this.ScratchBlocks.ProgramMode.UPLOAD);
        }
        this._programMode = this.props.isRealtimeMode;
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }
    }
    updateToolbox () {
        this.toolboxUpdateTimeout = false;

        this.props.onToolboxWillUpdate();

        const categoryId = this.workspace.toolbox_.getSelectedCategoryId();
        const offset = this.workspace.toolbox_.getCategoryScrollOffset();
        this.workspace.updateToolbox(this.props.toolboxXML);
        this._renderedToolboxXML = this.props.toolboxXML;

        // In order to catch any changes that mutate the toolbox during "normal runtime"
        // (variable changes/etc), re-enable toolbox refresh.
        // Using the setter function will rerender the entire toolbox which we just rendered.
        this.workspace.toolboxRefreshEnabled_ = true;

        const currentCategoryPos = this.workspace.toolbox_.getCategoryPositionById(categoryId);
        const currentCategoryLen = this.workspace.toolbox_.getCategoryLengthById(categoryId);
        if (offset < currentCategoryLen) {
            this.workspace.toolbox_.setFlyoutScrollPos(currentCategoryPos + offset);
        } else {
            this.workspace.toolbox_.setFlyoutScrollPos(currentCategoryPos);
        }

        const queue = this.toolboxUpdateQueue;
        this.toolboxUpdateQueue = [];
        queue.forEach(fn => fn());
    }

    withToolboxUpdates (fn) {
        // if there is a queued toolbox update, we need to wait
        if (this.toolboxUpdateTimeout) {
            this.toolboxUpdateQueue.push(fn);
        } else {
            fn();
        }
    }

    attachVM () {
        this.workspace.addChangeListener(this.props.vm.blockListener);
        this.flyoutWorkspace = this.workspace
            .getFlyout()
            .getWorkspace();
        this.flyoutWorkspace.addChangeListener(this.props.vm.flyoutBlockListener);
        this.flyoutWorkspace.addChangeListener(this.props.vm.monitorBlockListener);
        this.props.vm.addListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.addListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.addListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.addListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.addListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.addListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.addListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.addListener('SCRATCH_EXTENSION_ADDED', this.handleScratchExtensionAdded);
        this.props.vm.addListener('SCRATCH_EXTENSION_REMOVED', this.handleScratchExtensionRemoved);
        this.props.vm.addListener('DEVICE_EXTENSION_ADDED', this.handleDeviceExtensionAdded);
        this.props.vm.addListener('DEVICE_EXTENSION_REMOVED', this.handleDeviceExtensionRemoved);
        this.props.vm.addListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.addListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.addListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.addListener('CODE_NEED_UPDATE', this.onCodeNeedUpdate);
        this.props.vm.addListener('TOOLBOX_UPLOAD_FINISH', this.handleToolboxUploadFinish);
    }
    detachVM () {
        this.props.vm.removeListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.removeListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.removeListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.removeListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.removeListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.removeListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.removeListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.removeListener('SCRATCH_EXTENSION_ADDED', this.handleScratchExtensionAdded);
        this.props.vm.removeListener('SCRATCH_EXTENSION_REMOVED', this.handleScratchExtensionRemoved);
        this.props.vm.removeListener('DEVICE_EXTENSION_ADDED', this.handleDeviceExtensionAdded);
        this.props.vm.removeListener('DEVICE_EXTENSION_REMOVED', this.handleDeviceExtensionRemoved);
        this.props.vm.removeListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.removeListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.removeListener('CODE_NEED_UPDATE', this.onCodeNeedUpdate);
        this.props.vm.removeListener('TOOLBOX_UPLOAD_FINISH', this.handleToolboxUploadFinish);
    }

    updateToolboxBlockValue (id, value) {
        this.withToolboxUpdates(() => {
            const block = this.workspace
                .getFlyout()
                .getWorkspace()
                .getBlockById(id);
            if (block) {
                block.inputList[0].fieldRow[0].setValue(value);
            }
        });
    }

    onTargetsUpdate () {
        if (this.props.vm.editingTarget && this.workspace.getFlyout()) {
            ['glide', 'move', 'set'].forEach(prefix => {
                this.updateToolboxBlockValue(`${prefix}x`, Math.round(this.props.vm.editingTarget.x).toString());
                this.updateToolboxBlockValue(`${prefix}y`, Math.round(this.props.vm.editingTarget.y).toString());
            });
        }
    }
    onWorkspaceMetricsChange () {
        const target = this.props.vm.editingTarget;
        if (target && target.id) {
            // Dispatch updateMetrics later, since onWorkspaceMetricsChange may be (very indirectly)
            // called from a reducer, i.e. when you create a custom procedure.
            // TODO: Is this a vehement hack?
            setTimeout(() => {
                this.props.updateMetrics({
                    targetID: target.id,
                    scrollX: this.workspace.scrollX,
                    scrollY: this.workspace.scrollY,
                    scale: this.workspace.scale
                });
            }, 0);
        }
    }
    onScriptGlowOn (data) {
        this.workspace.glowStack(data.id, true);
    }
    onScriptGlowOff (data) {
        this.workspace.glowStack(data.id, false);
    }
    onBlockGlowOn (data) {
        this.workspace.glowBlock(data.id, true);
    }
    onBlockGlowOff (data) {
        this.workspace.glowBlock(data.id, false);
    }
    onVisualReport (data) {
        this.workspace.reportValue(data.id, data.value);
    }
    onActivateColorPicker (callback) {
        if (this.props.isRealtimeMode) {
            this.props.onActivateColorPicker(callback);
        }
    }
    getToolboxXML () {
        // Use try/catch because this requires digging pretty deep into the VM
        // Code inside intentionally ignores several error situations (no stage, etc.)
        // Because they would get caught by this try/catch
        try {
            let {editingTarget: target, runtime} = this.props.vm;
            const stage = runtime.getTargetForStage();
            if (!target) target = stage; // If no editingTarget, use the stage

            const stageCostumes = stage.getCostumes();
            const targetCostumes = target.getCostumes();
            const targetSounds = target.getSounds();
            const dynamicBlocksXML = this.props.vm.runtime.getBlocksXML(target);

            const device = this.props.deviceData.find(item => item.deviceId === this.props.deviceId);

            return makeToolboxXML(false, device, target.isStage, target.id, dynamicBlocksXML,
                this.props.isRealtimeMode,
                targetCostumes[targetCostumes.length - 1].name,
                stageCostumes[stageCostumes.length - 1].name,
                targetSounds.length > 0 ? targetSounds[targetSounds.length - 1].name : ''
            );
        } catch (error) {
            log.warn(error);
            return null;
        }
    }
    onWorkspaceUpdate (data) {
        // When we change sprites, update the toolbox to have the new sprite's blocks
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }

        if (this.props.vm.editingTarget && !this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            this.onWorkspaceMetricsChange();
        }

        // Remove and reattach the workspace listener (but allow flyout events)
        this.workspace.removeChangeListener(this.props.vm.blockListener);
        const dom = this.ScratchBlocks.Xml.textToDom(data.xml);
        try {
            this.ScratchBlocks.Xml.clearWorkspaceAndLoadFromXml(dom, this.workspace);
        } catch (error) {
            // The workspace is likely incomplete. What did update should be
            // functional.
            //
            // Instead of throwing the error, by logging it and continuing as
            // normal lets the other workspace update processes complete in the
            // gui and vm, which lets the vm run even if the workspace is
            // incomplete. Throwing the error would keep things like setting the
            // correct editing target from happening which can interfere with
            // some blocks and processes in the vm.
            if (error.message) {
                error.message = `Workspace Update Error: ${error.message}`;
            }
            log.error(error);
        }
        this.workspace.addChangeListener(this.props.vm.blockListener);

        if (this.props.vm.editingTarget && this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            const {scrollX, scrollY, scale} = this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id];
            this.workspace.scrollX = scrollX;
            this.workspace.scrollY = scrollY;
            this.workspace.scale = scale;
            this.workspace.resize();
        }

        // Clear the undo state of the workspace since this is a
        // fresh workspace and we don't want any changes made to another sprites
        // workspace to be 'undone' here.
        this.workspace.clearUndo();
    }
    handleScratchExtensionAdded (extensionInfo) {
        const {deviceId, categoryInfoArray} = extensionInfo;

        if (deviceId) {
            const dev = this.props.deviceData.find(ext => ext.deviceId === deviceId);
            this.props.onDeviceSelected(dev.deviceId, dev.name, dev.type);
            this.ScratchBlocks.Device.setDevice(dev.deviceId, dev.type);
            if (dev.defaultBaudRate) {
                this.props.onSetBaudrate(dev.defaultBaudRate);
            }

            const supportUploadMode = dev.programMode.includes('upload');
            const supportRealtimeMode = dev.programMode.includes('realtime');

            // eslint-disable-next-line no-negated-condition
            if (supportUploadMode && supportRealtimeMode) {
                this.props.onSetSupportSwitchMode(true);

                const defaultProgramMode = dev.defaultProgramMode;
                if (dev.programMode.includes(defaultProgramMode)) {
                    if (defaultProgramMode === 'upload') {
                        this.props.vm.runtime.setRealtimeMode(false);
                    } else {
                        this.props.vm.runtime.setRealtimeMode(true);
                    }
                }

            } else {
                if (supportUploadMode) {
                    this.props.vm.runtime.setRealtimeMode(false);
                } else {
                    this.props.vm.runtime.setRealtimeMode(true);
                }
                this.props.onSetSupportSwitchMode(false);
            }

            if (this.deviceFakeToolboxHead) {
                this.deviceFakeToolboxHead = '';
            } else {
                this.deviceFakeToolboxHead = ' ';
            }
        }

        categoryInfoArray.forEach(categoryInfo => {
            const defineBlocks = blockInfoArray => {
                if (blockInfoArray && blockInfoArray.length > 0) {
                    const staticBlocksJson = [];
                    const dynamicBlocksInfo = [];
                    blockInfoArray.forEach(blockInfo => {
                        if (blockInfo.info && blockInfo.info.isDynamic) {
                            dynamicBlocksInfo.push(blockInfo);
                        } else if (blockInfo.json) {
                            staticBlocksJson.push(blockInfo.json);
                        }
                        // otherwise it's a non-block entry such as '---'
                    });

                    this.ScratchBlocks.defineBlocksWithJsonArray(staticBlocksJson);
                    dynamicBlocksInfo.forEach(blockInfo => {
                        // This is creating the block factory / constructor -- NOT a specific instance of the block.
                        // The factory should only know static info about the block: the category info and the opcode.
                        // Anything else will be picked up from the XML attached to the block instance.
                        const extendedOpcode = `${categoryInfo.id}_${blockInfo.info.opcode}`;
                        const blockDefinition =
                            defineDynamicBlock(this.ScratchBlocks, categoryInfo, blockInfo, extendedOpcode);
                        this.ScratchBlocks.Blocks[extendedOpcode] = blockDefinition;
                    });
                }
            };

            // openblock-blocks implements a menu or custom field as a special kind of block ("shadow" block)
            // these actually define blocks and MUST run regardless of the UI state
            defineBlocks(
                Object.getOwnPropertyNames(categoryInfo.customFieldTypes)
                    .map(fieldTypeName => categoryInfo.customFieldTypes[fieldTypeName].scratchBlocksDefinition));
            defineBlocks(categoryInfo.menus);
            defineBlocks(categoryInfo.blocks);
        });

        // Update the toolbox with new blocks if possible, use timeout to let props update first
        setTimeout(() => {
            const toolboxXML = this.getToolboxXML();
            if (toolboxXML) {
                this.props.updateToolboxState(this.deviceFakeToolboxHead + toolboxXML);
            }
        }, 0);
    }
    handleScratchExtensionRemoved (extensionInfo) {
        if (extensionInfo && extensionInfo.deviceId) {
            this.props.onDeviceSelected(null, null, null);
            this.props.vm.runtime.setRealtimeMode(true);
            this.props.onSetSupportSwitchMode(false);
        }
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(this.deviceFakeToolboxHead + toolboxXML);
        }
    }
    handleDeviceExtensionAdded (deviceExtensionsRegister) {
        if (deviceExtensionsRegister.defineMessages) {
            this.ScratchBlocks = deviceExtensionsRegister.defineMessages(this.ScratchBlocks);
        }
        if (deviceExtensionsRegister.defineGenerators) {
            this.ScratchBlocks = deviceExtensionsRegister.defineGenerators(this.ScratchBlocks);
        }
        if (deviceExtensionsRegister.defineBlocks) {
            this.ScratchBlocks = deviceExtensionsRegister.defineBlocks(this.ScratchBlocks);
        }

        this.setLocale(false);

        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }
    }
    handleDeviceExtensionRemoved () {
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }
    }
    handleBlocksInfoUpdate (extensionInfo) {
        // @todo Later we should replace this to avoid all the warnings from redefining blocks.
        this.handleScratchExtensionAdded(extensionInfo);
    }
    handleCategorySelected (categoryId) {
        const extension = extensionData.find(ext => ext.extensionId === categoryId);
        if (extension && extension.launchPeripheralConnectionFlow) {
            this.handleConnectionModalStart();
        }

        this.withToolboxUpdates(() => {
            this.workspace.toolbox_.setSelectedCategoryById(categoryId);
        });
    }
    handleDeviceSelected (categoryId) {
        const device = this.props.deviceData.find(ext => ext.deviceId === categoryId);

        if (device &&
            device.launchPeripheralConnectionFlow &&
            device.deviceId !== 'arduinoEsp32S3'
        ) {
            this.handleConnectionModalStart();
        }

        this.withToolboxUpdates(() => {
            this.workspace.toolbox_.setSelectedCategoryById(categoryId);
        });
    }
    setBlocks (blocks) {
        this.blocks = blocks;
    }
    overrideBlockMessages () {
        if (!this.ScratchBlocks || !this.ScratchBlocks.Msg) return;
        const Msg = this.ScratchBlocks.Msg;
        // Motion
        Msg.MOTION_MOVESTEPS = 'Di chuyển %1 bước';
        Msg.MOTION_TURNRIGHT = 'Xoay phải %1 %2 độ';
        Msg.MOTION_TURNLEFT = 'Xoay trái %1 %2 độ';

        Msg.MOTION_POINTINDIRECTION = 'Đặt hướng bằng %1';
        Msg.MOTION_POINTTOWARDS = 'Hướng về phía đối tượng %1';
        Msg.MOTION_POINTTOWARDS_POINTER = 'con trỏ chuột';
        Msg.MOTION_POINTTOWARDS_RANDOM = 'hướng ngẫu nhiên';
        Msg.MOTION_GOTO = 'Đi tới %1';
        Msg.MOTION_GOTO_POINTER = 'con trỏ chuột';
        Msg.MOTION_GOTO_RANDOM = 'vị trí ngẫu nhiên';
        Msg.MOTION_GOTOXY = 'Đi tới x: %1 y: %2';
        Msg.MOTION_GLIDESECSTOXY = 'Lướt %1 giây tới x: %2 y: %3';
        Msg.MOTION_GLIDETO = 'Lướt %1 giây tới %2';
        Msg.MOTION_GLIDETO_POINTER = 'con trỏ chuột';
        Msg.MOTION_GLIDETO_RANDOM = 'vị trí ngẫu nhiên';
        Msg.MOTION_CHANGEXBY = 'Thay đổi vị trí x %1';
        Msg.MOTION_SETX = 'Đặt vị trí x %1';
        Msg.MOTION_CHANGEYBY = 'Thay đổi vị trí y %1';
        Msg.MOTION_SETY = 'Đặt vị trí y %1';
        Msg.MOTION_IFONEDGEBOUNCE = 'Nếu va chạm với biên';
        Msg.MOTION_SETROTATIONSTYLE = 'Đặt kiểu xoay %1';
        Msg.MOTION_SETROTATIONSTYLE_LEFTRIGHT = 'trái-phải';
        Msg.MOTION_SETROTATIONSTYLE_DONTROTATE = 'không xoay';
        Msg.MOTION_SETROTATIONSTYLE_ALLAROUND = 'xung quanh';
        Msg.MOTION_XPOSITION = 'Vị trí x';
        Msg.MOTION_YPOSITION = 'Vị trí y';
        Msg.MOTION_DIRECTION = 'Hướng';
        Msg.MOTION_SCROLLRIGHT = 'cuộn phải %1';
        Msg.MOTION_SCROLLUP = 'cuộn lên %1';
        Msg.MOTION_ALIGNSCENE = 'căn chỉnh cảnh %1';
        Msg.MOTION_ALIGNSCENE_BOTTOMLEFT = 'góc trái dưới';
        Msg.MOTION_ALIGNSCENE_BOTTOMRIGHT = 'góc phải dưới';
        Msg.MOTION_ALIGNSCENE_MIDDLE = 'giữa';
        Msg.MOTION_ALIGNSCENE_TOPLEFT = 'góc trái trên';
        Msg.MOTION_ALIGNSCENE_TOPRIGHT = 'góc phải trên';
        Msg.MOTION_XSCROLL = 'cuộn x';
        Msg.MOTION_YSCROLL = 'cuộn y';
        Msg.MOTION_STAGE_SELECTED = 'Đã chọn nền: không có khối chuyển động';

        Msg.LOOKS_SAY = 'Nói %1';
        Msg.LOOKS_THINK = 'Nghĩ %1';

        // Control
        Msg.CONTROL_FOREVER = 'mãi mãi';
        Msg.CONTROL_REPEAT = 'Lặp lại %1 lần';
        Msg.CONTROL_IF = 'nếu %1 thì';
        Msg.CONTROL_ELSE = 'nếu không';
        Msg.CONTROL_STOP = 'dừng';
        Msg.CONTROL_STOP_ALL = 'tất cả';
        Msg.CONTROL_STOP_THIS = 'script này';
        Msg.CONTROL_STOP_OTHER = 'script khác trong đối tượng';
        Msg.CONTROL_WAIT = 'Chờ %1 giây';
        Msg.CONTROL_WAITUNTIL = 'chờ đến khi %1';
        Msg.CONTROL_REPEATUNTIL = 'lặp lại cho đến khi %1';
        Msg.CONTROL_WHILE = 'trong khi %1';
        Msg.CONTROL_FOREACH = 'với mỗi %1 trong %2';
        Msg.CONTROL_STARTASCLONE = 'khi bắt đầu là bản sao';
        Msg.CONTROL_CREATECLONEOF = 'tạo bản sao của %1';
        Msg.CONTROL_CREATECLONEOF_MYSELF = 'chính tôi';
        Msg.CONTROL_DELETETHISCLONE = 'xóa bản sao này';
        Msg.CONTROL_COUNTER = 'bộ đếm';
        Msg.CONTROL_INCRCOUNTER = 'tăng bộ đếm';
        Msg.CONTROL_CLEARCOUNTER = 'xóa bộ đếm';
        Msg.CONTROL_ALLATONCE = 'tất cả cùng lúc';

        // Sensing
        Msg.SENSING_TOUCHINGOBJECT = 'đang chạm %1?';
        Msg.SENSING_TOUCHINGOBJECT_POINTER = 'con trỏ chuột';
        Msg.SENSING_TOUCHINGOBJECT_EDGE = 'cạnh';
        Msg.SENSING_TOUCHINGCOLOR = 'chạm màu %1?';
        Msg.SENSING_COLORISTOUCHINGCOLOR = 'màu %1 đang chạm %2?';
        Msg.SENSING_DISTANCETO = 'khoảng cách tới %1';
        Msg.SENSING_DISTANCETO_POINTER = 'con trỏ chuột';
        Msg.SENSING_ASKANDWAIT = 'hỏi %1 và chờ';
        Msg.SENSING_ASK_TEXT = 'Bạn tên là gì?';
        Msg.SENSING_ANSWER = 'câu trả lời';
        Msg.SENSING_KEYPRESSED = 'phím %1 được nhấn?';
        Msg.SENSING_MOUSEDOWN = 'chuột đang nhấn?';
        Msg.SENSING_MOUSEX = 'tọa độ chuột x';
        Msg.SENSING_MOUSEY = 'tọa độ chuột y';
        Msg.SENSING_SETDRAGMODE = 'đặt chế độ kéo %1';
        Msg.SENSING_SETDRAGMODE_DRAGGABLE = 'có thể kéo';
        Msg.SENSING_SETDRAGMODE_NOTDRAGGABLE = 'không thể kéo';
        Msg.SENSING_LOUDNESS = 'độ lớn âm thanh';
        Msg.SENSING_LOUD = 'to?';
        Msg.SENSING_TIMER = 'bộ đếm thời gian';
        Msg.SENSING_RESETTIMER = 'đặt lại bộ đếm thời gian';
        Msg.SENSING_OF = '%1 của %2';
        Msg.SENSING_OF_XPOSITION = 'vị trí x';
        Msg.SENSING_OF_YPOSITION = 'vị trí y';
        Msg.SENSING_OF_DIRECTION = 'hướng';
        Msg.SENSING_OF_COSTUMENUMBER = 'hình dạng #';
        Msg.SENSING_OF_COSTUMENAME = 'tên hình dạng';
        Msg.SENSING_OF_SIZE = 'kích thước';
        Msg.SENSING_OF_VOLUME = 'âm lượng';
        Msg.SENSING_OF_BACKDROPNUMBER = 'phông nền #';
        Msg.SENSING_OF_BACKDROPNAME = 'tên phông nền';
        Msg.SENSING_OF_STAGE = 'Nền';
        Msg.SENSING_CURRENT = 'hiện tại %1';
        Msg.SENSING_CURRENT_YEAR = 'năm';
        Msg.SENSING_CURRENT_MONTH = 'tháng';
        Msg.SENSING_CURRENT_DATE = 'ngày';
        Msg.SENSING_CURRENT_DAYOFWEEK = 'thứ';
        Msg.SENSING_CURRENT_HOUR = 'giờ';
        Msg.SENSING_CURRENT_MINUTE = 'phút';
        Msg.SENSING_CURRENT_SECOND = 'giây';
        Msg.SENSING_DAYSSINCE2000 = 'số ngày từ 2000';
        Msg.SENSING_USERNAME = 'tên người dùng';
        Msg.SENSING_USERID = 'ID người dùng';

        // Sound
        Msg.SOUND_PLAY = 'Bắt đầu âm thanh %1';
        Msg.SOUND_PLAYUNTILDONE = 'Phát âm thanh %1 đến hết';
        Msg.SOUND_STOPALLSOUNDS = 'Dừng tất cả âm thanh';
        Msg.SOUND_SETEFFECTO = 'Đặt hiệu ứng %1 thành %2';
        Msg.SOUND_CHANGEEFFECTBY = 'Thay đổi hiệu ứng %1 theo %2';
        Msg.SOUND_CLEAREFFECTS = 'Xóa hiệu ứng âm thanh';
        Msg.SOUND_EFFECTS_PITCH = 'cao độ';
        Msg.SOUND_EFFECTS_PAN = 'pan trái/phải';
        Msg.SOUND_CHANGEVOLUMEBY = 'Thay đổi âm lượng theo %1';
        Msg.SOUND_SETVOLUMETO = 'Đặt âm lượng thành %1%';
        Msg.SOUND_VOLUME = 'Âm lượng';
        Msg.SOUND_RECORD = 'ghi âm...';

        // Events
        Msg.EVENT_WHENARDUINOBEGIN = 'khi Chương trình bắt đầu';
        Msg.EVENT_WHENMICROPYTHONBEGIN = 'khi MicroPython bắt đầu';
        Msg.EVENT_WHENMICROBITBEGIN = 'khi Microbit bắt đầu';
        Msg.EVENT_WHENMICROBITBUTTONPRESSED = 'khi nút %1 được nhấn';
        Msg.EVENT_WHENMICROBITPINBEINGTOUCHED = 'khi chân %1 được chạm';
        Msg.EVENT_WHENMICROBITGESTURE = 'khi cử chỉ %1';
        Msg.EVENT_WHENFLAGCLICKED = 'Khi %1 được nhấn';
        Msg.EVENT_WHENTHISSPRITECLICKED = 'khi đối tượng này được nhấn';
        Msg.EVENT_WHENSTAGECLICKED = 'khi nền được nhấn';
        Msg.EVENT_WHENTOUCHINGOBJECT = 'khi đối tượng này chạm %1';
        Msg.EVENT_WHENBROADCASTRECEIVED = 'khi tôi nhận %1';
        Msg.EVENT_WHENBACKDROPSWITCHESTO = 'khi phông nền chuyển sang %1';
        Msg.EVENT_WHENGREATERTHAN = 'khi %1 > %2';
        Msg.EVENT_WHENGREATERTHAN_TIMER = 'bộ đếm thời gian';
        Msg.EVENT_WHENGREATERTHAN_LOUDNESS = 'độ lớn âm thanh';
        Msg.EVENT_BROADCAST = 'phát thông điệp %1';
        Msg.EVENT_BROADCASTANDWAIT = 'phát thông điệp %1 và chờ';
        Msg.EVENT_WHENKEYPRESSED = 'Khi phím %1 được nhấn';
        Msg.EVENT_WHENMICROBITGESTURE_SHAKEN = 'lắc';
        Msg.EVENT_WHENMICROBITGESTURE_TILTEDUPWARD = 'nghiêng lên';
        Msg.EVENT_WHENMICROBITGESTURE_TILTEDDOWNWARD = 'nghiêng xuống';
        Msg.EVENT_WHENMICROBITGESTURE_TILTEDDLEFTWARD = 'nghiêng trái';
        Msg.EVENT_WHENMICROBITGESTURE_TILTEDDRIGHTWARD = 'nghiêng phải';
        Msg.EVENT_WHENMICROBITGESTURE_FACEUP = 'ngửa mặt';
        Msg.EVENT_WHENMICROBITGESTURE_FACEDOWN = 'úp mặt';
        Msg.EVENT_WHENMICROBITGESTURE_FREEFALL = 'rơi tự do';
        Msg.EVENT_WHENKEYPRESSED_SPACE = 'dấu cách';
        Msg.EVENT_WHENKEYPRESSED_LEFT = 'mũi tên trái';
        Msg.EVENT_WHENKEYPRESSED_RIGHT = 'mũi tên phải';
        Msg.EVENT_WHENKEYPRESSED_DOWN = 'mũi tên xuống';
        Msg.EVENT_WHENKEYPRESSED_UP = 'mũi tên lên';
        Msg.EVENT_WHENKEYPRESSED_ANY = 'bất kỳ';

        // Hình dạng
        Msg.LOOKS_SAYFORSECS = 'Nói %1 trong %2 giây';
        Msg.LOOKS_SAY = 'Nói %1';
        Msg.LOOKS_HELLO = 'Hello!';
        Msg.LOOKS_THINKFORSECS = 'Nghĩ %1 trong %2 giây';
        Msg.LOOKS_THINK = 'Nghĩ %1';
        Msg.LOOKS_HMM = 'Hmm...';
        Msg.LOOKS_SHOW = 'Hiện';
        Msg.LOOKS_HIDE = 'Ẩn';
        Msg.LOOKS_HIDEALLSPRITES = 'Ẩn tất cả các hình';
        Msg.LOOKS_EFFECT_COLOR = 'màu sắc';
        Msg.LOOKS_EFFECT_FISHEYE = 'mắt cá';
        Msg.LOOKS_EFFECT_WHIRL = 'xoáy';
        Msg.LOOKS_EFFECT_PIXELATE = 'làm rỗ';
        Msg.LOOKS_EFFECT_MOSAIC = 'khảm';
        Msg.LOOKS_EFFECT_BRIGHTNESS = 'độ sáng';
        Msg.LOOKS_EFFECT_GHOST = 'ma';
        Msg.LOOKS_CHANGEEFFECTBY = 'Thay đổi hiệu ứng %1 theo %2';
        Msg.LOOKS_SETEFFECTTO = 'Đặt %1 hiệu ứng thành %2';
        Msg.LOOKS_SWITCHBACKDROPTO = 'Đổi phông nền thành %1';
        Msg.LOOKS_SWITCHBACKDROPTOANDWAIT = 'Đổi phông nền thành %1 và chờ';
        Msg.LOOKS_NEXTBACKDROP_BLOCK = 'Phông nền tiếp theo';
        Msg.LOOKS_PREVIOUSBACKDROP = 'Phông nền trước';
        Msg.LOOKS_SWITCHCOSTUMETO = 'Đổi hình dạng thành %1';
        Msg.LOOKS_NEXTCOSTUME = 'Hình dạng tiếp theo';
        Msg.LOOKS_CHANGESIZEBY = 'Thay đổi kích thước theo %1';
        Msg.LOOKS_SETSIZETO = 'Đặt kích thước thành %1';
        Msg.LOOKS_CHANGEEFFECTBY = 'Thay đổi hiệu ứng %1 theo %2';
        Msg.LOOKS_CLEARGRAPHICEFFECTS = 'Xóa hiệu ứng hình ảnh';
        Msg.LOOKS_GOTOFRONTBACK = 'Đưa lên %1';
        Msg.LOOKS_GOTOFRONTBACK_FRONT = 'Trước';
        Msg.LOOKS_GOTOFRONTBACK_BACK = 'Sau';
        Msg.LOOKS_GOFORWARDBACKWARDLAYERS = 'Đi %1 %2 lớp';
        Msg.LOOKS_GOFORWARDBACKWARDLAYERS_FORWARD = 'về phía trước';
        Msg.LOOKS_GOFORWARDBACKWARDLAYERS_BACKWARD = 'về phía sau';
        Msg.LOOKS_BACKDROPNUMBERNAME = 'Phồng nền %1';
        Msg.LOOKS_COSTUMENUMBERNAME = 'Hình dạng %1';
        Msg.LOOKS_BACKDROPNUMBERNAME = 'Phông nền %1';
        Msg.LOOKS_SIZE = 'Kích thước';
        Msg.SOUND_PLAYUNTILDONE = 'Phát âm thanh %1 đến hết';
        Msg.SOUND_STOPALLSOUNDS = 'Dừng tất cả âm thanh';
        Msg.SOUND_CHANGEEFFECTBY = 'Thay đổi hiệu ứng âm thanh %1 theo %2';
        Msg.SOUND_EFFECTS_PITCH = 'cao độ';
        Msg.SOUND_EFFECTS_PAN = 'pan trái/phải';
        Msg.SOUND_SETEFFECTO = 'Đặt %1 hiệu ứng thành %2';
        Msg.SOUND_CLEAREFFECTS = 'Xóa hiệu ứng âm thanh';
        Msg.SOUND_CHANGEVOLUMEBY = 'Thay đổi âm lượng theo %1';
        Msg.SOUND_SETVOLUMETO = 'Đặt âm lượng thành %1%';
        Msg.SOUND_VOLUME = 'Âm lượng';

        // Data (Variables/Lists)
        Msg.DATA_SETVARIABLETO = 'đặt %1 thành %2';
        Msg.DATA_CHANGEVARIABLEBY = 'thay đổi %1 thêm %2';
        Msg.DATA_SHOWVARIABLE = 'hiện biến %1';
        Msg.DATA_HIDEVARIABLE = 'ẩn biến %1';
        Msg.DATA_ADDTOLIST = 'thêm %1 vào %2';
        Msg.DATA_DELETEOFLIST = 'xóa %1 của %2';
        Msg.DATA_DELETEALLOFLIST = 'xóa tất cả %1';
        Msg.DATA_INSERTATLIST = 'chèn %1 tại %2 của %3';
        Msg.DATA_REPLACEITEMOFLIST = 'thay thế mục %1 của %2 bằng %3';
        Msg.DATA_ITEMOFLIST = 'mục %1 của %2';
        Msg.DATA_ITEMNUMOFLIST = 'số thứ tự của %1 trong %2';
        Msg.DATA_LENGTHOFLIST = 'độ dài của %1';
        Msg.DATA_LISTCONTAINSITEM = '%1 chứa %2?';
        Msg.DATA_SHOWLIST = 'hiện danh sách %1';
        Msg.DATA_HIDELIST = 'ẩn danh sách %1';
        Msg.DATA_INDEX_ALL = 'tất cả';
        Msg.DATA_INDEX_LAST = 'cuối cùng';
        Msg.DATA_INDEX_RANDOM = 'ngẫu nhiên';

        // Categories
        Msg.CATEGORY_MOTION = 'Chuyển động';
        Msg.CATEGORY_LOOKS = 'Hình dạng';
        Msg.CATEGORY_SOUND = 'Âm thanh';
        Msg.CATEGORY_EVENTS = 'Sự kiện';
        Msg.CATEGORY_CONTROL = 'Điều khiển';
        Msg.CATEGORY_SENSING = 'Cảm biến';
        Msg.CATEGORY_OPERATORS = 'Toán học';
        Msg.CATEGORY_VARIABLES = 'Biến số';
        Msg.CATEGORY_MYBLOCKS = 'Khối của tôi';

        // Context menu & editor strings
        Msg.COPY = 'Sao chép';
        Msg.PASTE = 'Dán';
        Msg.DUPLICATE = 'Nhân bản';
        Msg.DELETE = 'Xóa';
        Msg.ADD_COMMENT = 'Thêm ghi chú';
        Msg.REMOVE_COMMENT = 'Xóa ghi chú';
        Msg.DELETE_BLOCK = 'Xóa khối';
        Msg.DELETE_X_BLOCKS = 'Xóa %1 khối';
        Msg.DELETE_ALL_BLOCKS = 'Xóa tất cả %1 khối?';
        Msg.CLEAN_UP = 'Sắp xếp khối';
        Msg.HELP = 'Trợ giúp';
        Msg.UNDO = 'Hoàn tác';
        Msg.REDO = 'Làm lại';
        Msg.EDIT_PROCEDURE = 'Chỉnh sửa';
        Msg.SHOW_PROCEDURE_DEFINITION = 'Đi tới định nghĩa';
        Msg.WORKSPACE_COMMENT_DEFAULT_TEXT = 'Hãy nói gì đó...';
        Msg.COLOUR_HUE_LABEL = 'Màu';
        Msg.COLOUR_SATURATION_LABEL = 'Độ bão hòa';
        Msg.COLOUR_BRIGHTNESS_LABEL = 'Độ sáng';
        Msg.CHANGE_VALUE_TITLE = 'Thay đổi giá trị:';
        Msg.RENAME_VARIABLE = 'Đổi tên biến';
        Msg.RENAME_VARIABLE_TITLE = 'Đổi tên tất cả biến "%1" thành:';
        Msg.RENAME_VARIABLE_MODAL_TITLE = 'Đổi tên biến';
        Msg.NEW_VARIABLE = 'Tạo biến';
        Msg.NEW_VARIABLE_TITLE = 'Tên biến mới:';
        Msg.VARIABLE_MODAL_TITLE = 'Biến mới';
        Msg.VARIABLE_ALREADY_EXISTS = 'Đã tồn tại biến tên "%1".';
        Msg.VARIABLE_ALREADY_EXISTS_FOR_ANOTHER_TYPE = 'Đã tồn tại biến tên "%1" cho loại biến "%2".';
        Msg.DELETE_VARIABLE_CONFIRMATION = 'Xóa %1 lần sử dụng của biến "%2"?';
        Msg.CANNOT_DELETE_VARIABLE_PROCEDURE = 'Không thể xóa biến "%1" vì nó là một phần của định nghĩa hàm "%2"';
        Msg.DELETE_VARIABLE = 'Xóa biến "%1"';
        Msg.NEW_PROCEDURE = 'Tạo một Khối';
        Msg.PROCEDURE_ALREADY_EXISTS = 'Đã tồn tại thủ tục tên "%1".';
        Msg.PROCEDURE_DEFAULT_NAME = 'tên khối';
        Msg.PROCEDURE_USED = 'Để xóa một định nghĩa khối, hãy xóa tất cả chỗ sử dụng khối đó trước';
        Msg.NEW_LIST = 'Tạo danh sách';
        Msg.NEW_LIST_TITLE = 'Tên danh sách mới:';
        Msg.LIST_MODAL_TITLE = 'Danh sách mới';
        Msg.LIST_ALREADY_EXISTS = 'Đã tồn tại danh sách tên "%1".';
        Msg.RENAME_LIST_TITLE = 'Đổi tên tất cả danh sách "%1" thành:';
        Msg.RENAME_LIST_MODAL_TITLE = 'Đổi tên danh sách';
        Msg.DEFAULT_LIST_ITEM = 'vật';
        Msg.DELETE_LIST = 'Xóa danh sách "%1"';
        Msg.RENAME_LIST = 'Đổi tên danh sách';
        Msg.NEW_BROADCAST_MESSAGE = 'Thông điệp mới';
        Msg.NEW_BROADCAST_MESSAGE_TITLE = 'Tên thông điệp mới:';
        Msg.BROADCAST_MODAL_TITLE = 'Thông điệp mới';
        Msg.DEFAULT_BROADCAST_MESSAGE_NAME = 'thongdiep1';
    }
    handlePromptStart (message, defaultValue, callback, optTitle, optVarType) {
        const p = {prompt: {callback, message, defaultValue}};
        p.prompt.title = optTitle ? optTitle :
            this.ScratchBlocks.Msg.VARIABLE_MODAL_TITLE;
        p.prompt.varType = typeof optVarType === 'string' ?
            optVarType : this.ScratchBlocks.SCALAR_VARIABLE_TYPE;
        p.prompt.showVariableOptions = // This flag means that we should show variable/list options about scope
            optVarType !== this.ScratchBlocks.BROADCAST_MESSAGE_VARIABLE_TYPE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_VARIABLE_MODAL_TITLE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_LIST_MODAL_TITLE;
        p.prompt.showCloudOption = (optVarType === this.ScratchBlocks.SCALAR_VARIABLE_TYPE) && this.props.canUseCloud;
        this.setState(p);
    }
    handleConnectionModalStart () {
        this.props.onOpenConnectionModal();
    }
    handleStatusButtonUpdate () {
        this.ScratchBlocks.refreshStatusButtons(this.workspace);
    }
    workspaceToCode () {
        let code;
        try {
            const generatorName = getGeneratorNameFromDeviceType(this.props.deviceType);
            code = this.ScratchBlocks[generatorName].workspaceToCode(this.workspace);
        } catch (e) {
            code = e.message;
        }
        return code;
    }
    handleToolboxUploadFinish () {
        this.props.onToolboxDidUpdate();
    }
    onCodeNeedUpdate () {
        if (this.props.isCodeEditorLocked) {
            if (this.props.isRealtimeMode === false) {
                this.props.onSetCodeEditorValue(this.workspaceToCode());
            }
        } else {
            this.props.onCodeEditorIsUnlocked();
        }
    }
    handleOpenSoundRecorder () {
        this.props.onOpenSoundRecorder();
    }

    /*
     * Pass along information about proposed name and variable options (scope and isCloud)
     * and additional potentially conflicting variable names from the VM
     * to the variable validation prompt callback used in openblock-blocks.
     */
    handlePromptCallback (input, variableOptions) {
        this.state.prompt.callback(
            input,
            this.props.vm.runtime.getAllVarNamesOfType(this.state.prompt.varType),
            variableOptions);
        this.handlePromptClose();
    }
    handlePromptClose () {
        this.setState({prompt: null});
    }
    handleCustomProceduresClose (data) {
        this.props.onRequestCloseCustomProcedures(data);
        const ws = this.workspace;
        ws.refreshToolboxSelection_();
        ws.toolbox_.scrollToCategoryById('myBlocks');
    }
    handleDrop (dragInfo) {
        fetch(dragInfo.payload.bodyUrl)
            .then(response => response.json())
            .then(blocks => this.props.vm.shareBlocksToTarget(blocks, this.props.vm.editingTarget.id))
            .then(() => {
                this.props.vm.refreshWorkspace();
                this.updateToolbox(); // To show new variables/custom blocks
            });
    }
    render () {
        /* eslint-disable no-unused-vars */
        const {
            anyModalVisible,
            canUseCloud,
            customProceduresVisible,
            deviceData,
            deviceId,
            deviceLibraryVisible,
            deviceType,
            peripheralName,
            extensionLibraryVisible,
            options,
            stageSize,
            vm,
            isCodeEditorLocked,
            isRealtimeMode,
            isRtl,
            isVisible,
            onActivateColorPicker,
            onDeviceSelected,
            onOpenConnectionModal,
            onOpenSoundRecorder,
            onToolboxWillUpdate,
            onToolboxDidUpdate,
            updateToolboxState,
            onActivateCustomProcedures,
            onCodeEditorIsUnlocked,
            onRequestCloseExtensionLibrary,
            onRequestCloseDeviceLibrary,
            onRequestCloseCustomProcedures,
            onSetCodeEditorValue,
            onSetSupportSwitchMode,
            onSetBaudrate,
            toolboxXML,
            updateMetrics: updateMetricsProp,
            workspaceMetrics,
            ...props
        } = this.props;
        /* eslint-enable no-unused-vars */
        return (
            <React.Fragment>
                <DroppableBlocks
                    componentRef={this.setBlocks}
                    onDrop={this.handleDrop}
                    {...props}
                />
                {this.state.prompt ? (
                    <Prompt
                        defaultValue={this.state.prompt.defaultValue}
                        isStage={!!(vm.runtime.getEditingTarget() && vm.runtime.getEditingTarget().isStage)}
                        showListMessage={this.state.prompt.varType === this.ScratchBlocks.LIST_VARIABLE_TYPE}
                        label={this.state.prompt.message}
                        showCloudOption={this.state.prompt.showCloudOption}
                        showVariableOptions={this.state.prompt.showVariableOptions}
                        title={this.state.prompt.title}
                        vm={vm}
                        onCancel={this.handlePromptClose}
                        onOk={this.handlePromptCallback}
                    />
                ) : null}
                {deviceLibraryVisible ? (
                    <DeviceLibrary
                        vm={vm}
                        onDeviceSelected={this.handleDeviceSelected}
                        onRequestClose={onRequestCloseDeviceLibrary}
                    />
                ) : null}
                {extensionLibraryVisible ? (
                    <ExtensionLibrary
                        vm={vm}
                        onCategorySelected={this.handleCategorySelected}
                        onRequestClose={onRequestCloseExtensionLibrary}
                    />
                ) : null}
                {customProceduresVisible ? (
                    <CustomProcedures
                        options={{
                            media: options.media
                        }}
                        onRequestClose={this.handleCustomProceduresClose}
                    />
                ) : null}
            </React.Fragment>
        );
    }
}

Blocks.propTypes = {
    anyModalVisible: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    customProceduresVisible: PropTypes.bool,
    deviceData: PropTypes.instanceOf(Array).isRequired,
    deviceId: PropTypes.string,
    deviceType: PropTypes.string,
    peripheralName: PropTypes.string,
    deviceLibraryVisible: PropTypes.bool,
    extensionLibraryVisible: PropTypes.bool,
    isCodeEditorLocked: PropTypes.bool.isRequired,
    isRealtimeMode: PropTypes.bool,
    isRtl: PropTypes.bool,
    isVisible: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.oneOfType([
        PropTypes.objectOf(PropTypes.string),
        PropTypes.shape({
            format: PropTypes.func,
            message: PropTypes.string
        })
    ]),
    onActivateColorPicker: PropTypes.func,
    onActivateCustomProcedures: PropTypes.func,
    onCodeEditorIsUnlocked: PropTypes.func,
    onDeviceSelected: PropTypes.func,
    onOpenConnectionModal: PropTypes.func,
    onOpenSoundRecorder: PropTypes.func,
    onToolboxWillUpdate: PropTypes.func,
    onToolboxDidUpdate: PropTypes.func,
    onRequestCloseCustomProcedures: PropTypes.func,
    onRequestCloseExtensionLibrary: PropTypes.func,
    onRequestCloseDeviceLibrary: PropTypes.func,
    options: PropTypes.shape({
        media: PropTypes.string,
        zoom: PropTypes.shape({
            controls: PropTypes.bool,
            wheel: PropTypes.bool,
            startScale: PropTypes.number
        }),
        colours: PropTypes.shape({
            workspace: PropTypes.string,
            flyout: PropTypes.string,
            toolbox: PropTypes.string,
            toolboxSelected: PropTypes.string,
            scrollbar: PropTypes.string,
            scrollbarHover: PropTypes.string,
            insertionMarker: PropTypes.string,
            insertionMarkerOpacity: PropTypes.number,
            fieldShadow: PropTypes.string,
            dragShadowOpacity: PropTypes.number
        }),
        comments: PropTypes.bool,
        collapse: PropTypes.bool
    }),
    onSetBaudrate: PropTypes.func.isRequired,
    onSetCodeEditorValue: PropTypes.func,
    onSetSupportSwitchMode: PropTypes.func,
    onShowMessageBox: PropTypes.func.isRequired,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    toolboxXML: PropTypes.string,
    updateMetrics: PropTypes.func,
    updateToolboxState: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired,
    workspaceMetrics: PropTypes.shape({
        targets: PropTypes.objectOf(PropTypes.object)
    })
};

Blocks.defaultOptions = {
    zoom: {
        controls: true,
        wheel: true,
        startScale: BLOCKS_DEFAULT_SCALE
    },
    grid: {
        spacing: 40,
        length: 2,
        colour: '#ddd'
    },
    colours: {
        workspace: '#F9F9F9',
        flyout: '#F9F9F9',
        toolbox: '#FFFFFF',
        toolboxSelected: '#E9EEF2',
        scrollbar: '#CECDCE',
        scrollbarHover: '#CECDCE',
        insertionMarker: '#000000',
        insertionMarkerOpacity: 0.2,
        fieldShadow: 'rgba(255, 255, 255, 0.3)',
        dragShadowOpacity: 0.6
    },
    comments: true,
    collapse: false,
    sounds: false
};

Blocks.defaultProps = {
    isVisible: true,
    options: Blocks.defaultOptions
};

const mapStateToProps = state => ({
    anyModalVisible: (
        Object.keys(state.scratchGui.modals).some(key => state.scratchGui.modals[key]) ||
        state.scratchGui.mode.isFullScreen
    ),
    deviceData: state.scratchGui.deviceData.deviceData,
    deviceId: state.scratchGui.device.deviceId,
    deviceType: state.scratchGui.device.deviceType,
    peripheralName: state.scratchGui.connectionModal.peripheralName,
    deviceLibraryVisible: state.scratchGui.modals.deviceLibrary,
    extensionLibraryVisible: state.scratchGui.modals.extensionLibrary,
    isCodeEditorLocked: state.scratchGui.code.isCodeEditorLocked,
    isRealtimeMode: state.scratchGui.programMode.isRealtimeMode,
    isRtl: state.locales.isRtl,
    locale: state.locales.locale,
    messages: state.locales.messages,
    toolboxXML: state.scratchGui.toolbox.toolboxXML,
    customProceduresVisible: state.scratchGui.customProcedures.active,
    workspaceMetrics: state.scratchGui.workspaceMetrics
});

const mapDispatchToProps = dispatch => ({
    onActivateColorPicker: callback => dispatch(activateColorPicker(callback)),
    onActivateCustomProcedures: (data, callback) => dispatch(activateCustomProcedures(data, callback)),
    onDeviceSelected: (id, name, type) => {
        dispatch(setDeviceId(id));
        dispatch(setDeviceName(name));
        dispatch(setDeviceType(type));
    },
    onOpenConnectionModal: () => {
        dispatch(openConnectionModal());
    },
    onOpenSoundRecorder: () => {
        dispatch(activateTab(SOUNDS_TAB_INDEX));
        dispatch(openSoundRecorder());
    },
    onRequestCloseExtensionLibrary: () => {
        dispatch(closeExtensionLibrary());
    },
    onRequestCloseDeviceLibrary: () => {
        dispatch(closeDeviceLibrary());
    },
    onRequestCloseCustomProcedures: data => {
        dispatch(deactivateCustomProcedures(data));
    },
    onSetBaudrate: baudrate => dispatch(setBaudrate(baudrate)),
    onToolboxWillUpdate: () => {
        dispatch(setIsUpdating(true));
    },
    onToolboxDidUpdate: () => {
        dispatch(setIsUpdating(false));
    },
    updateToolboxState: toolboxXML => {
        dispatch(updateToolbox(toolboxXML));
    },
    updateMetrics: metrics => {
        dispatch(updateMetrics(metrics));
    },
    onSetCodeEditorValue: value => {
        dispatch(setCodeEditorValue(value));
    },
    onSetSupportSwitchMode: state => dispatch(setSupportSwitchMode(state)),
    onCodeEditorIsUnlocked: () => showAlertWithTimeout(dispatch, 'codeEditorIsUnlocked')
});

export default errorBoundaryHOC('Blocks')(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Blocks)
);
